/* eslint-disable max-lines */
import {createSelector} from 'reselect';
import {HtoSettingsType} from 'reducers/hto/settings/settingsType';
// @ts-ignore
import {getQuoteItems, getActiveCartItems} from 'selectors/cart/cart';
// @ts-ignore
import {getIsCategoryFulfilled, getIsContactLensesCategory} from 'selectors/category/category';
import {getIsFiltersDataFulfilled, getFilters} from 'selectors/filters/filters';

import {HTO_SEGMENTS} from 'constants/homeTryOn/homeTryOn';
import {QUOTE_ITEM_LENS_TYPE} from 'constants/lensType';
import {HTO_CODE_FILTER} from 'constants/category';
import {FilterDataType} from 'constants/flow/flowTypes';
import {ApplicationStoreType} from 'reducers';
import {QuoteItemType} from 'constants/flow/flowTypesCart';
import {isHtoEnabled} from 'constants/flags/flags';

import {increaseByLensesType} from './helpers';

/**
 * Get Home Try-on settings
 * @param {ApplicationStoreType} store - Application store
 * @return {HtoSettingsType} Home Try-on settings
 */
export const getSettings = (store: ApplicationStoreType): HtoSettingsType => store.hto.settings;

/**
 * Get limit of single vision products in Home Try-on order
 * @param {ApplicationStoreType} store - Application store
 * @return {number} limit
 */
export const getSingleVisionHtoLimit = (store: ApplicationStoreType) =>
    getSettings(store).single_vision.limit_order_items;

/**
 * Get period of free Home Try-on for single vision products
 * @param {ApplicationStoreType} store - Application store
 * @return {number} period
 */
export const getSingleVisionHtoPeriod = (store: ApplicationStoreType) =>
    getSettings(store).single_vision.hto_period;

/**
 * Get status "is HTO (Home Try-on) category"
 * @param {ApplicationStoreType} store - Application store
 * @type {boolean} Status "is HTO (Home Try-on) category"
 */
export const isHomeTryOnCategory = createSelector(getFilters, ({items}: FilterDataType): boolean =>
    items.some(({code}: FilterDataType) => code === HTO_CODE_FILTER),
);

/**
 * If cart items have inActive and productAsHto and isHtoEnabled
 * @param {ApplicationStoreType} store - store
 * @returns {boolean} boolean
 */
export const isHomeTryOnCart = createSelector(
    getQuoteItems,
    (items): boolean =>
        isHtoEnabled() && items.some(({productAsHto, active}) => active && productAsHto),
);

/**
 * Is cart HTO selected
 * @param {ApplicationStoreType} store - Application store.
 * @returns {boolean} - Is HTO selected
 */
export const isSelectedCartHTO = (store: ApplicationStoreType): boolean =>
    Boolean(store.hto.cart && store.hto.cart.selected);

/**
 * Get not eligible quote items in cart.
 *
 * @param {ApplicationStoreType} - Application store.
 * @returns {Array<QuoteItemType>} - Not eligible items.
 */
export const getNotEligibleQuoteItems = createSelector(
    getQuoteItems,
    (quoteItems: Array<QuoteItemType>) => quoteItems.filter(item => !item.is_eligible_for_hto),
);

/**
 * Get HTO segment
 * @param {ApplicationStoreType} store - Application store
 * @return {string} HTO segment name
 */
export const getHtoSegment = createSelector(getQuoteItems, (items: QuoteItemType[]) => {
    const isAllEligibleForHto = items.every(item => item.is_eligible_for_hto);
    const isAnyAddedAsHto = items.some(item => item.productAsHto);

    if (isAnyAddedAsHto) {
        return HTO_SEGMENTS.USE_HTO;
    }

    if (!isAnyAddedAsHto && !isAllEligibleForHto) {
        return HTO_SEGMENTS.NO_USE_HTO;
    }

    return HTO_SEGMENTS.UNKNOWN;
});

/**
 * Returns true if there is at least one item in quote that added as HTO product, false otherwise
 */
export const hasHtoItemsInCart = createSelector(getQuoteItems, (items: QuoteItemType[]) => {
    return items.some(item => item.productAsHto);
});

/**
 * Get is available hto elements on category
 *
 * @param {ApplicationStoreType} store - Application store
 * @return {boolean} is available HTO elements
 */
export const getIsAvailableOnCategory = createSelector(
    getIsCategoryFulfilled,
    getIsContactLensesCategory,
    getIsFiltersDataFulfilled,
    (isLoaded, isContactLensesCategory, isFiltersDataLoaded): boolean =>
        isLoaded && !isContactLensesCategory && isFiltersDataLoaded,
);

/**
 * Get length single vision
 * @param {ApplicationStoreType} store - Application store.
 * @returns {number} - items sum
 */
export const getLengthSingleVision = createSelector(getActiveCartItems, (items): number =>
    increaseByLensesType(QUOTE_ITEM_LENS_TYPE.SINGLE_VISION, items),
);

/**
 * Get length single progressive
 * @param {ApplicationStoreType} store - Application store.
 * @returns {number} - items sum
 */
export const getLengthProgressive = createSelector(getActiveCartItems, (items): number =>
    increaseByLensesType(QUOTE_ITEM_LENS_TYPE.PROGRESSIVE, items),
);

/**
 * Get is available widget HTO
 *
 * @param {ApplicationStoreType} store - Application store
 * @return {boolean} is available widget HTO
 */
export const getIsAvailableWidget = createSelector(
    getIsAvailableOnCategory,
    isHomeTryOnCategory,
    (isShow, isHto): boolean => isShow && isHto,
);
